import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import { Avatar, Box, Card, Grid, MenuItem, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";

import { NumbersListSubcomponent } from "./components/NumbersListSubcomponent";
import { CompanyNumberItem } from "./types";
import { useContactCenterUsageSummaryQuery } from "../billing/hooks/useContactCenterUsageSummaryQuery";
import { ContactCenterUsageCards } from "./components/ContactCenterUsageCards";

export const ContactCenterDashboard: React.FC = () => {
  const [numbersList, setNumbersList] = useState<CompanyNumberItem[]>([]);

  const totalCount = numbersList.length;
  const activeCount = numbersList.filter((n) => n.status.toLowerCase() === "active").length;
  const pendingCount = numbersList.filter((n) => n.status.toLowerCase() === "pending").length;
  const unassignedCount = numbersList.filter((n) => n.status.toLowerCase() === "unassigned").length;

  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();

    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const { data: usageSummary, isLoading: isUsageSummaryLoading, isError: isUsageSummaryError } = useContactCenterUsageSummaryQuery(billingPeriod);

  const billingPeriods = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - index);

      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    });
  }, []);

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, bgcolor: "var(--fx-surface-sunken, #F8FAFC)", minHeight: "100vh" }}>
      {/* Dashboard Top Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: "var(--fx-colors-info-50, #EFF6FF)", color: "#0284C7", width: 48, height: 48 }}>
          <HeadsetMicIcon fontSize="medium" />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} color="var(--fx-surface-text, #0F172A)">
            Contact Center Dashboard
          </Typography>
          <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)">
            Superadmin administration of phone lines, virtual receptionist numbers, and provider routing
          </Typography>
        </Box>
      </Box>

      {/* KPI Cards Header */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "none",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)" textTransform="uppercase">
              Total Phone Lines
            </Typography>
            <Typography variant="h4" fontWeight={800} color="var(--fx-surface-text, #0F172A)" mt={0.5}>
              {totalCount}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <PhoneInTalkIcon sx={{ fontSize: 16, color: "#0284C7" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                Global directory
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "none",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#15803D" textTransform="uppercase">
              Active Lines
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#16A34A" mt={0.5}>
              {activeCount}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16A34A" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                Operational lines
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "none",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#B45309" textTransform="uppercase">
              Pending Lines
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#D97706" mt={0.5}>
              {pendingCount}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <PendingActionsIcon sx={{ fontSize: 16, color: "#D97706" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                In setup or porting
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "none",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)" textTransform="uppercase">
              Unassigned Lines
            </Typography>
            <Typography variant="h4" fontWeight={800} color="var(--fx-surface-textsecondary, #64748B)" mt={0.5}>
              {unassignedCount}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <HelpOutlineIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                Available in pool
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: "var(--fx-surface-text, #0F172A)" }}>Contact Center Usage</Typography>

          <Typography sx={{ fontSize: "0.85rem", color: "var(--fx-surface-textsecondary, #64748B)" }}>Usage shown only for the selected monthly billing period.</Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Billing period"
          value={billingPeriod}
          onChange={(event) => setBillingPeriod(event.target.value)}
          sx={{
            minWidth: 170,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: "10px",
          }}
        >
          {billingPeriods.map((period) => (
            <MenuItem key={period} value={period}>
              {period}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <ContactCenterUsageCards usage={usageSummary} isLoading={isUsageSummaryLoading} isError={isUsageSummaryError} />

      <NumbersListSubcomponent
        onNumbersLoaded={setNumbersList}
        usageItems={usageSummary?.items ?? []}
        usageCurrency={usageSummary?.currency ?? "USD"}
        billingPeriod={usageSummary?.billingPeriod ?? billingPeriod}
      />
    </Box>
  );
};

export default ContactCenterDashboard;
